import React, { useEffect, useState } from "react";
import { StagePage } from "src/stage/StageUi";
import { useNavigate } from "react-router-dom";
import conversationService from "src/services/conversationService";
import { currentConnection } from "src/features/Broadcasts/broadcastApi";
import ContactsHeader from "./component/ContactHeader";
import ContactsFilters from "./component/ContactFilters";
import ContactsTable from "./component/ContactsTable";
import ContactsPagination from "./component/ConatctsPagination";
import ContactCreateModal from "./component/CreateContactModal";
import useContacts from "./hooks/useContacts";
import { CircleAlert, RotateCcw } from "lucide-react";
import ImportContactsModal from "./component/ImportContactsModal";
import ContactDetailModal from "./component/ContactDetailModal";

function Contact() {
  const {
    contacts,
    allContacts,
    canManageAllContacts,
    loading,
    error,
    pagination,
    filters,
    setFilters,
    resetFilters,
    changePage,
    refreshContacts,
    createContact,
    updateContact,
    deleteContact,
    workspaceId,
  } = useContacts();

  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactModalMode, setContactModalMode] = useState("create");
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactSubmitError, setContactSubmitError] = useState("");
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailContact, setDetailContact] = useState(null);

  useEffect(() => {
    if (
      detailContact &&
      !allContacts.some((contact) => contact.id === detailContact.id)
    ) {
      setDetailContact(null);
      setDetailModalOpen(false);
    }
    if (
      selectedContact &&
      !allContacts.some((contact) => contact.id === selectedContact.id)
    ) {
      setSelectedContact(null);
      setContactModalOpen(false);
    }
  }, [allContacts, detailContact, selectedContact]);

  /* =========================================================
     CONTACT CREATE
  ========================================================= */

  const handleCreateContact = () => {
    setContactSubmitError("");
    setSelectedContact(null);
    setContactModalMode("create");
    setContactModalOpen(true);
  };

  /* =========================================================
     CONTACT EDIT
  ========================================================= */

  const handleEditContact = (contact) => {
    if (!contact) {
      return;
    }

    setContactSubmitError("");
    setSelectedContact(contact);
    setContactModalMode("update");
    setContactModalOpen(true);
  };

  /* =========================================================
     CONTACT MODAL CLOSE
  ========================================================= */

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
    setSelectedContact(null);
    setContactModalMode("create");
    setContactSubmitError("");
  };

  /* =========================================================
     CONTACT SUBMIT
  ========================================================= */

  const handleContactSubmit = async (payload) => {
    if (!payload) {
      return;
    }

    try {
      if (contactModalMode === "update" && selectedContact?.id) {
        await updateContact?.(selectedContact.id, payload);
      } else {
        await createContact?.(payload);
      }

      setContactModalOpen(false);
      setSelectedContact(null);
      setContactModalMode("create");
      await refreshContacts?.();
    } catch (submitError) {
      const message =
        submitError?.response?.data?.message ||
        "Failed to save contact. Please try again.";
      setContactSubmitError(message);
    }
  };

  /* =========================================================
     SELECTION
  ========================================================= */

  const allCurrentPageSelected =
    contacts.length > 0 &&
    contacts.every((contact) => selectedContacts.includes(contact.id));

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedContacts(contacts.map((contact) => contact.id));

      return;
    }

    setSelectedContacts([]);
  };

  const handleSelectContact = (contactId, checked) => {
    setSelectedContacts((previous) => {
      if (checked) {
        if (previous.includes(contactId)) {
          return previous;
        }

        return [...previous, contactId];
      }

      return previous.filter((id) => id !== contactId);
    });
  };

  /* =========================================================
     FILTERS
  ========================================================= */

  const handleSearchChange = (value) => {
    setFilters?.((previous) => ({
      ...previous,
      search: value,
    }));
  };

  const handleTagChange = (value) => {
    setFilters?.((previous) => ({
      ...previous,
      tag: value,
    }));
  };

  const handleDateChange = (value) => {
    setFilters?.((previous) => ({
      ...previous,
      dateRange: value,
    }));
  };

  const handleSortChange = (value) => {
    setFilters?.((previous) => ({
      ...previous,
      sortBy: value,
    }));
  };

  const handleQuickFilter = (value) => {
    setFilters?.((previous) => ({
      ...previous,
      quickFilter: value,
    }));
  };

  /* =========================================================
     IMPORT
  ========================================================= */

  const handleOpenImportModal = () => setImportModalOpen(true);

  const handleCloseImportModal = () => setImportModalOpen(false);

  /* =========================================================
     DELETE CONTACT
  ========================================================= */

  const handleDeleteContact = async (contact) => {
    if (!contact?.id) {
      return;
    }

    try {
      await deleteContact(contact.id);

      setSelectedContacts((previous) =>
        previous.filter((id) => id !== contact.id),
      );
    } catch (deleteError) {
      console.error("Failed to delete contact:", deleteError);
    }
  };
  /* =========================================================
     CONTACT ACTION
  ========================================================= */

  // AUTO-WA-39: "Message" opens the person's chat, starting one if needed.
  // A new chat opens on "Send a template" when the 24-hour window is closed.
  const navigate = useNavigate();
  const openChat = async (contact) => {
    const connection = currentConnection();
    if (!connection || !contact?.id) return;
    const link = (id) => `/conversation?${new URLSearchParams({ conversation: id, workspace: connection.workspaceId, number: connection.phoneNumberId })}`;
    try {
      const found = await conversationService.index(connection.phoneNumberId, { search: contact.phone_number, per_page: 10 });
      const existing = (found?.data?.data || []).find((item) => (item.contact?.id || item.whatsapp_contact_id) === contact.id);
      if (existing) { navigate(link(existing.id)); return; }
      const created = await conversationService.create(connection.phoneNumberId, { whatsapp_contact_id: contact.id });
      const id = created?.data?.data?.id;
      navigate(id ? link(id) : `/conversation?search=${encodeURIComponent(contact.phone_number || "")}`);
    } catch {
      navigate(`/conversation?search=${encodeURIComponent(contact.phone_number || "")}`);
    }
  };

  const handleContactAction = (action, contact) => {
    switch (action) {
      case "view":
      case "open":
        setDetailContact(contact);
        setDetailModalOpen(true);
        break;

      case "message":
        openChat(contact);
        break;

      case "edit":
        handleEditContact(contact);
        break;

      case "delete":
        handleDeleteContact(contact);
        break;

      default:
        break;
    }
  };

  /* =========================================================
     PAGINATION
  ========================================================= */

  const currentPage = pagination?.current_page ?? 1;
  const lastPage = pagination?.last_page ?? 1;
  const totalContacts = pagination?.total ?? 0;
  const showingFrom =
    pagination?.from ??
    (currentPage ? (currentPage - 1) * (pagination?.per_page || 20) + 1 : 0);
  const showingTo =
    pagination?.to ??
    Math.min(
      currentPage
        ? currentPage * (pagination?.per_page || 20)
        : contacts.length,
      totalContacts,
    );
  const handlePageChange = (page) => {
    if (loading || page === currentPage || page < 1 || page > lastPage) return;
    changePage?.(page);
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <StagePage className="wa-legacy" wide label="Contacts">
      <>
          <ContactsHeader
            totalContacts={totalContacts}
            selectedCount={selectedContacts.length}
            onBroadcastSelected={() => navigate("/broadcast/new", { state: { contactIds: selectedContacts } })}
            onClearSelection={() => setSelectedContacts([])}
            onImport={canManageAllContacts ? handleOpenImportModal : undefined}
            onAddContact={
              canManageAllContacts ? handleCreateContact : undefined
            }
            loading={loading}
          />

          <section className="ws-card relative overflow-visible">
            <ContactsFilters
              filters={filters}
              totalContacts={totalContacts}
              loading={loading}
              onSearchChange={handleSearchChange}
              onTagChange={handleTagChange}
              onDateChange={handleDateChange}
              onSortChange={handleSortChange}
              onQuickFilter={handleQuickFilter}
              onReset={resetFilters}
            />

            {error ? (
              <div className="mx-4 mt-4 flex items-center gap-3 rounded-2xl border border-[#f0cccc] bg-[#fff7f7] px-3.5 py-3 sm:mx-5 dark:border-[#61383b] dark:bg-[#2a1d20]">
                <CircleAlert size={17} className="shrink-0 text-[#c45f5f]" />
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-bold text-[#7d3f43] dark:text-[#f0b0b4]">
                    Unable to load contacts
                  </p>
                  <p className="mt-0.5 truncate text-[9px] font-medium text-[#a06d70] dark:text-[#ba8589]">
                    {error?.message ||
                      "Something went wrong while loading contacts."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={refreshContacts}
                  disabled={loading}
                  className="inline-flex h-8 items-center gap-1.5 rounded-full border border-[#e7b9bc] bg-white px-3 text-[9px] font-bold text-[#a44f55] transition-colors hover:bg-[#fff0f0] disabled:opacity-50 dark:border-[#71464a] dark:bg-[#352326] dark:text-[#e29a9e]"
                >
                  <RotateCcw size={11} strokeWidth={2} />
                  Retry
                </button>
              </div>
            ) : null}

            <ContactsTable
              contacts={contacts}
              loading={loading}
              selectedContacts={selectedContacts}
              allCurrentPageSelected={allCurrentPageSelected}
              sortBy={filters.sortBy}
              onSortChange={handleSortChange}
              onSelectAll={handleSelectAll}
              onSelectContact={handleSelectContact}
              onAction={handleContactAction}
            />

            <ContactsPagination
              currentPage={currentPage}
              lastPage={lastPage}
              total={totalContacts}
              from={showingFrom}
              to={showingTo}
              loading={loading}
              onPageChange={handlePageChange}
            />
          </section>
      </>

      <ContactCreateModal
        workspaceId={workspaceId}
        open={contactModalOpen}
        loading={loading}
        mode={contactModalMode}
        error={contactSubmitError}
        contact={selectedContact}
        onClose={handleCloseContactModal}
        onSubmit={handleContactSubmit}
      />

      <ImportContactsModal
        open={importModalOpen}
        onClose={handleCloseImportModal}
        onImported={refreshContacts}
      />

      <ContactDetailModal
        open={detailModalOpen}
        contact={detailContact}
        onClose={() => setDetailModalOpen(false)}
        onEdit={(contact) => {
          setDetailModalOpen(false);
          handleEditContact(contact);
        }}
        onMessage={(contact) => handleContactAction("message", contact)}
        onTagsChanged={() => refreshContacts?.()}
      />
    </StagePage>
  );
}

export default Contact;
